// TelcoTrack – filtr s více hodnotami (checkboxy v rozbalovacím panelu)

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FilterMultiOption,
  filterMultiOptionsByQuery,
  filterMultiSummary,
  showsFilterOptionSearch,
} from '../../../utils/filter-multi.util';
import {
  claimFilterOverlay,
  releaseFilterOverlay,
} from '../../../utils/filter-overlay-coordinator.util';
import { filterOverlayPanelStyle } from '../../../utils/filter-overlay-position.util';
import type { IconName } from '../../../utils/icon-vocabulary';

/** Icon trigger id. Glyphs live in `icon-vocabulary` (`ICON_MEANING`). */
export type FilterMultiSelectIcon = '' | IconName;

@Component({
  selector: 'app-filter-multi-select',
  templateUrl: './filter-multi-select.component.html',
  styleUrls: ['./filter-multi-select.component.scss'],
})
export class FilterMultiSelectComponent implements OnDestroy {
  @Input() label = '';
  @Input() options: FilterMultiOption[] = [];
  @Input() selected: string[] = [];
  @Input() emptyLabel = 'Všechny';
  /** Compact default for text triggers; icon mode ignores this. */
  @Input() controlClass = 'min-w-[5rem] max-w-[9rem]';
  @Input() panelMaxHeight = '16rem';
  @Input() disabled = false;
  /** Compact icon trigger (calendar / people / polygon / objects / home / status / roles / materials / warehouse-from / warehouse-to / construction / drawing / regions / cross / spider / equipment / flag / tag / pin). Empty keeps the labeled dropdown. */
  @Input() icon: FilterMultiSelectIcon = '';
  /** Radio behaviour — exactly one value; no clear action / selection badge (e.g. map výkres). */
  @Input() singleSelect = false;
  /** Always show in-panel search, even when there are ≤ 10 options (e.g. building picker). */
  @Input() alwaysShowSearch = false;
  /**
   * When set, footer shows „Zapnout vše“ / „Vypnout vše“ for this subset only
   * (e.g. all `uzemi:*` values) without clearing the rest of the selection.
   */
  @Input() bulkToggleValues: readonly string[] | null = null;
  @Input() bulkToggleAllLabel = 'Zapnout vše';
  @Input() bulkToggleNoneLabel = 'Vypnout vše';

  @Output() selectedChange = new EventEmitter<string[]>();

  @ViewChild('triggerBtn') private triggerBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelEl') private panelEl?: ElementRef<HTMLElement>;
  @ViewChild('optionSearchInput') private optionSearchInput?: ElementRef<HTMLInputElement>;

  panelOpen = false;
  /** Textové zúžení možností v panelu. Prázdné = celý seznam. */
  optionQuery = '';

  /**
   * Panel se renderuje jako `position: fixed` a po otevření se přesune přímo do `document.body`
   * (portál). Tím spolehlivě unikne stacking kontextu i ořezu rodiče — `position: fixed` se totiž
   * „uvězní" v jakémkoli předkovi s `transform`/`filter`/`contain` a tabulka/paginace pod řádkem
   * filtrů ho pak překryje, takže kliknutí padají „skrz" na ně místo na checkboxy. Z `body` (bez
   * takových předků) se poloha počítá vůči viewportu z `getBoundingClientRect` tlačítka.
   */
  panelStyle: { [klic: string]: string } = {};

  /** Zda je panel právě přesunutý do `document.body`, a kam ho vrátit při zavření. */
  private panelInBody = false;
  private panelOriginalParent: Node | null = null;
  private destroyed = false;
  /**
   * True while the pointer is down inside the open panel. Scroll/resize must not
   * reposition mid-gesture — moving the fixed panel under the cursor cancels the
   * checkbox `click`/`change` (dropdown closes via document:click, selection never
   * sticks). Common after the parent modal grows (e.g. photo preview) and becomes
   * scrollable.
   */
  private panelPointerActive = false;

  private readonly reposition = (event?: Event): void => {
    if (this.panelPointerActive) return;
    // Scrolling the options list itself must not move the fixed panel.
    const target = event?.target;
    if (target instanceof Node && this.panelEl?.nativeElement.contains(target)) return;
    this.updatePanelPosition();
  };

  @HostBinding('class.tt-filter-multi-select--open')
  get hostOpenClass(): boolean {
    return this.panelOpen;
  }

  @HostBinding('class.tt-filter-multi-select--icon')
  get hostIconClass(): boolean {
    return !!this.icon;
  }

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get summaryText(): string {
    const empty = this.emptyLabel || this.label || 'Všechny';
    return filterMultiSummary(this.selected, this.options, empty);
  }

  /** In-panel search names the list. The panel title is the only visible heading. */
  get optionSearchPlaceholder(): string {
    const what = (this.label || this.emptyLabel || '').trim();
    return what ? `Hledat: ${what}` : 'Hledat…';
  }

  /** Under the closed single-select trigger (e.g. full polygon name under KIA). */
  get summarySubtitle(): string {
    if (!this.singleSelect || this.selected.length !== 1) return '';
    const hit = this.options.find((option) => option.value === this.selected[0]);
    return (hit?.subtitle ?? '').trim();
  }

  get hasSelection(): boolean {
    return this.selected.length > 0;
  }

  get showIconBadge(): boolean {
    return !this.singleSelect && this.hasSelection;
  }

  get showClearFooter(): boolean {
    return !this.singleSelect && this.hasSelection;
  }

  get showBulkToggleFooter(): boolean {
    return !this.singleSelect && (this.bulkToggleValues?.length ?? 0) > 0;
  }

  get showFooter(): boolean {
    return this.showClearFooter || this.showBulkToggleFooter;
  }

  get bulkToggleAllSelected(): boolean {
    const values = this.bulkToggleValues;
    if (!values?.length) return false;
    const selected = new Set(this.selected);
    return values.every((v) => selected.has(v));
  }

  get bulkToggleAnySelected(): boolean {
    const values = this.bulkToggleValues;
    if (!values?.length) return false;
    const selected = new Set(this.selected);
    return values.some((v) => selected.has(v));
  }

  get iconAriaLabel(): string {
    return this.label || this.emptyLabel || 'Filtr';
  }

  get iconTitle(): string {
    const name = this.label || this.emptyLabel;
    return this.hasSelection ? `${name}: ${this.summaryText}` : name;
  }

  /** Hledání u seznamů delších než 10 položek, nebo vždy když `alwaysShowSearch`. */
  get showOptionSearch(): boolean {
    return this.alwaysShowSearch || showsFilterOptionSearch(this.options.length);
  }

  get visibleOptions(): FilterMultiOption[] {
    if (!this.showOptionSearch) return this.options;
    return filterMultiOptionsByQuery(this.options, this.optionQuery, this.selected);
  }

  get optionsEmptyMessage(): string {
    if (this.options.length === 0) return 'Žádné možnosti';
    return 'Žádná shoda';
  }

  isChecked(value: string): boolean {
    return this.selected.includes(value);
  }

  /**
   * `trackBy` pro seznam možností. Volající často vrací `options` z getteru = nové pole nových
   * objektů při každé change detection. Bez stabilního trackBy by `*ngFor` na každém CD zničil a
   * znovu vytvořil `<label>`/`<input>`, takže `mousedown`/`mouseup` padnou na různé (zničené) uzly
   * a prohlížeč nikdy nevygeneruje `click` → checkbox nejde zaškrtnout. Klíčujeme podle hodnoty.
   */
  trackByValue(_index: number, opt: FilterMultiOption): string {
    return opt.value;
  }

  /** Hlavička skupiny při první viditelné položce s daným `group` (nebo změně skupiny). */
  shouldShowGroupHeader(opt: FilterMultiOption, index: number): boolean {
    if (!opt.group) return false;
    if (index <= 0) return true;
    return this.visibleOptions[index - 1]?.group !== opt.group;
  }

  togglePanel(): void {
    if (this.disabled) return;
    if (this.panelOpen) {
      this.closePanel();
      return;
    }
    // Closes Objekty / HP / Dig. / other filter overlays (shared coordinator).
    claimFilterOverlay(this);
    this.panelOpen = true;
    // Polohu nastavíme hned (z rectu tlačítka), aby panel renderoval rovnou na správném místě
    // (bez „bliknutí"). Po vykreslení ho přesuneme do body a doladíme dle skutečné šířky panelu.
    this.updatePanelPosition();
    requestAnimationFrame(() => {
      this.movePanelToBody();
      this.updatePanelPosition();
      if (this.showOptionSearch) {
        this.optionSearchInput?.nativeElement.focus();
      }
    });
    // Při scrollu jen přepočítat pozici — panel se zavře až při kliku mimo (document:click).
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  closePanel(): void {
    if (!this.panelOpen) return;
    // Panel vrátíme zpět do hostitele, aby ho `*ngIf` korektně odstranil (jinak osiří v body).
    this.restorePanelToHost();
    this.panelOpen = false;
    this.panelPointerActive = false;
    releaseFilterOverlay(this);
    this.teardownReposition();
    this.optionQuery = '';
    // Map is OnPush; a sibling (HP/Dig.) may close us outside this component's event path.
    if (!this.destroyed) this.cdr.detectChanges();
  }

  /** Přesun panelu do `document.body` (portál), ať unikne stacking/ořezu předků. */
  private movePanelToBody(): void {
    const panel = this.panelEl?.nativeElement;
    if (!panel || this.panelInBody) return;
    this.panelOriginalParent = panel.parentNode;
    document.body.appendChild(panel);
    this.panelInBody = true;
  }

  /** Vrátí panel zpět pod původního rodiče (před odstraněním přes `*ngIf`). */
  private restorePanelToHost(): void {
    if (!this.panelInBody) return;
    const panel = this.panelEl?.nativeElement;
    if (panel && this.panelOriginalParent) {
      this.panelOriginalParent.appendChild(panel);
    }
    this.panelInBody = false;
    this.panelOriginalParent = null;
  }

  private updatePanelPosition(): void {
    const btn = this.triggerBtn?.nativeElement;
    if (!btn) return;
    const minWidth = this.icon || this.showOptionSearch
      ? 288
      : Math.round(btn.getBoundingClientRect().width);
    this.panelStyle = filterOverlayPanelStyle(btn, this.panelEl?.nativeElement, minWidth);
  }

  private teardownReposition(): void {
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.teardownReposition();
    releaseFilterOverlay(this);
    // Komponenta mizí, zatímco je panel ještě v body → odstraníme ho ručně, ať neosiří.
    if (this.panelInBody) {
      this.panelEl?.nativeElement.remove();
      this.panelInBody = false;
      this.panelOriginalParent = null;
    }
  }

  /**
   * Single-select: commit on mousedown (before focus/scroll can move the portaled
   * panel). `preventDefault` stops focus-induced scroll which would fire the
   * capture scroll listener and jump the panel under the cursor.
   */
  onOptionMouseDown(value: string, event: MouseEvent): void {
    if (!this.singleSelect || this.disabled) return;
    event.preventDefault();
    this.commitSingleSelect(value);
  }

  onCheckboxChange(value: string, checked: boolean): void {
    if (this.singleSelect) {
      // Mouse path already committed in `onOptionMouseDown`. Keyboard (Space) still
      // lands here via `change` without a prior mousedown on the label.
      if (checked && this.selected[0] !== value) {
        this.commitSingleSelect(value);
      }
      return;
    }
    const set = new Set(this.selected);
    if (checked) set.add(value);
    else set.delete(value);
    const next = [...set];
    this.selected = next;
    this.selectedChange.emit([...next]);
  }

  private commitSingleSelect(value: string): void {
    const next = [value];
    // Local sync before teardown: OnPush parents may not have pushed `[selected]`
    // before `closePanel()` → `detectChanges()`, otherwise the trigger stays on
    // `emptyLabel` even though `selectedChange` already fired.
    this.selected = next;
    this.selectedChange.emit([...next]);
    // Defer close so this gesture + parent CD finish (avoids click-through).
    setTimeout(() => this.closePanel(), 0);
  }

  onPanelPointerDown(event: MouseEvent): void {
    // Zabrání „kliknutí skrz" panel na prvky pod ním (paginace, tabulka).
    event.stopPropagation();
    this.panelPointerActive = true;
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onDocumentPointerUp(): void {
    this.panelPointerActive = false;
  }

  clearSelection(): void {
    this.selected = [];
    this.selectedChange.emit([]);
  }

  bulkToggleAll(): void {
    const values = this.bulkToggleValues;
    if (!values?.length) return;
    const set = new Set(this.selected);
    for (const v of values) set.add(v);
    const next = [...set];
    this.selected = next;
    this.selectedChange.emit([...next]);
  }

  bulkToggleNone(): void {
    const values = this.bulkToggleValues;
    if (!values?.length) return;
    const remove = new Set(values);
    const next = this.selected.filter((v) => !remove.has(v));
    this.selected = next;
    this.selectedChange.emit([...next]);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') event.preventDefault();
    if (event.key !== 'Escape') event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelOpen) return;
    const target = event.target as Node;
    // Panel může být přesunutý do body, proto kontrolujeme i jeho obsah, ne jen hostitele.
    const insideHost = this.host.nativeElement.contains(target);
    const insidePanel = this.panelEl?.nativeElement.contains(target) ?? false;
    if (!insideHost && !insidePanel) {
      this.closePanel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closePanel();
  }
}
